import { Head, Link, router } from '@inertiajs/react'
import { TaxModuleType } from './types.ts'
import { Activity, ArrowLeft, DollarSign, Edit, Trash2, TrendingUp, Users } from 'lucide-react'
import { taxModuleIconByName } from './icons.ts'

interface ShowProps {
  tax_module: TaxModuleType
  stats: {
    total_taxpayers: number
    total_collections: number
    average_per_taxpayer: number
    collection_rate: number
  }
  taxes: {
    id: string
    name: string
    code: string
    active: boolean
    category_name: string | null
    tax_type_name: string | null
  }[]
  can_destroy?: boolean
}

export default function Show({ tax_module, stats, taxes, can_destroy = false }: ShowProps) {
  const ModuleIcon = taxModuleIconByName(tax_module.icon)
  const handleDestroy = () => {
    if (!window.confirm(`Supprimer le module fiscal "${tax_module.name}" ?`)) return

    router.delete(`/admin/tax_modules/${tax_module.id}`)
  }

  return (
    <>
      <Head title={`Module fiscal #${tax_module.id}`} />

      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/tax_modules">
              <button className="rounded-lg border border-border p-2 text-foreground hover:bg-secondary transition-colors">
                <ArrowLeft className="h-5 w-5" />
              </button>
            </Link>
            <div>
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-3">
                  <ModuleIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{tax_module.name}</h1>
                  <p className="text-sm text-muted-foreground">{tax_module.code}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href={`/admin/tax_modules/${tax_module.id}/edit`}>
              <button className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">
                <Edit className="h-4 w-4" />
                Editer
              </button>
            </Link>
            {can_destroy && (
              <button
                type="button"
                className="flex items-center gap-2 rounded-lg border border-destructive px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
                onClick={handleDestroy}
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </button>
            )}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total des contribuables</p>
                <p className="mt-2 text-3xl font-bold text-foreground">{stats?.total_taxpayers ?? 0}</p>
              </div>
              <Users className="h-8 w-8 text-info" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total des collectes</p>
                <p className="mt-2 text-3xl font-bold text-success">{(stats?.total_collections ?? 0).toLocaleString()} FCFA</p>
              </div>
              <DollarSign className="h-8 w-8 text-success" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Moyenne par contribuable</p>
                <p className="mt-2 text-3xl font-bold text-warning">{(stats?.average_per_taxpayer ?? 0).toLocaleString()} FCFA</p>
              </div>
              <TrendingUp className="h-8 w-8 text-warning" />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Taux de recouvrement</p>
                <p className="mt-2 text-3xl font-bold text-success">{stats?.collection_rate ?? 0}%</p>
              </div>
              <Activity className="h-8 w-8 text-success" />
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">Taxes de ce module</h2>
          <div className="mt-4 overflow-hidden rounded-lg border border-border">
            <table className="w-full">
              <thead className="border-b border-border bg-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Nom</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Code</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Catégorie</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Type de taxe</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Statut</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {taxes.length > 0 ? taxes.map((tax) => (
                  <tr key={tax.id} className="hover:bg-secondary/50">
                    <td className="px-4 py-3 text-sm text-foreground">{tax.name}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{tax.code}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{tax.category_name || '-'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">{tax.tax_type_name || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${tax.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {tax.active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <Link href={`/admin/taxes/${tax.id}`} className="text-primary hover:text-primary/80">
                        Voir
                      </Link>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={6}>
                      Aucune taxe trouvée pour ce module.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

    </>
  )
}
